//Components
import Avatar from '../../../../avatar/Avatar';
import DataGrid from '../../../../table/DataGrid';
import Chip from '@mui/material/Chip';
import FilterButtonUser from '../FilterUsers/Button';
import { Checkbox, Skeleton, Typography, FormControlLabel, Radio } from '@mui/material';
import React, { useEffect, useState } from 'react';
//Other dependencies
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useDispatch, useSelector } from 'react-redux';
import { checkedUserIds } from '../../../../../store/actions/dataEntrySurveyBuilderActions';
import { SURVEY_TYPE_ABOUT_USERS, SURVEY_TYPE_STANDARD } from '../../constants/typeSurvey';

dayjs.extend(utc);
dayjs.extend(timezone);

const TableUser = ({
  type,
  filteredRoles,
  isSaving,
  list = [],
  dataRoles,
  isLoadingRoles,
  dataYears,
  isLoadingYears,
  included,
  setIncluded
}) => {
  const dispatch = useDispatch();
  const permissions = useSelector((state) => state?.session?.permissions?.dataentrysurveys ?? {});
  const skip = useSelector((state) => state?.datatable?.surveyUsersList?.skip ?? 0);
  const rowsPerPage = useSelector((state) => state?.datatable?.surveyUsersList?.rowsPerPage ?? 100);
  const columnsTable = useSelector((state) => state?.datatable?.surveyUsersList?.columnsTable ?? []);
  const module = useSelector((state) => state.dataEntrySurveyBuilder || {});
  const surveysBuilder = module?.surveysBuilder ?? {};
  const { checkedIds } = surveysBuilder;
  const totalFilter = surveysBuilder?.totalFilterList || [];
  const loading = surveysBuilder?.loading;
  const selectedTemplate = surveysBuilder?.template || null;
  const totalRecords = surveysBuilder?.totalList || 0;
  const shouldDisableElements = loading || isSaving || isLoadingRoles || isLoadingYears;

  const onChecked = (ids) => dispatch(checkedUserIds(ids));

  const onCheckedAll = (checked) => {
    let idsToCheck = [];

    if (checked && checkedIds.length !== totalRecords) {
      idsToCheck = totalFilter.map((item) => String(item._id));
    }

    onChecked(idsToCheck);
  };

  const onCheckOne = (id, checked) => {
    if (checked) {
      onChecked([...checkedIds, id]);
    } else {
      onChecked(checkedIds?.filter((item) => item !== id));
    }
  };

  const shouldDisableFilter = !selectedTemplate || loading;

  return (
    <>
      <div className='absolute inset-0 bottom-8'>
        <div className='h-16 flex items-center justify-start gap-2'>
          <FilterButtonUser
            type={type}
            disabled={shouldDisableElements || !selectedTemplate}
            filteredRolesId={filteredRoles ?? []}
            dataRoles={dataRoles ?? []}
            dataYears={dataYears ?? []}
            isLoadingRoles={isLoadingRoles}
            isLoadingYears={isLoadingYears}
          />
          <Typography
            variant='inherit'
            className='font-bold capitalize'
            color='primary'
            align='right'
            component='p'
          >
            Show:
          </Typography>
          <div className='flex items-center gap-2'>
            <FormControlLabel
              control={
                <Radio
                  checked={included === 'all'}
                  name='all'
                  value={true}
                  onChange={() => setIncluded('all')}
                  color='primary'
                  disabled={shouldDisableFilter || checkedIds?.length === 0}
                />
              }
              label='All'
            />
            <FormControlLabel
              control={
                <Radio
                  checked={included === 'included'}
                  name='included'
                  value={false}
                  onChange={() => setIncluded('included')}
                  color='primary'
                  disabled={shouldDisableFilter || checkedIds?.length === 0}
                />
              }
              label='Included'
            />
            <FormControlLabel
              control={
                <Radio
                  checked={included === 'not-included'}
                  name='not-included'
                  value={false}
                  onChange={() => setIncluded('not-included')}
                  color='primary'
                  disabled={shouldDisableFilter || checkedIds?.length === 0}
                />
              }
              label='Not included'
            />
          </div>
        </div>
        <div className='top-16 absolute inset-0'>
          <DataGrid
            className='survey-builder-table'
            id='table-user-survey'
            rows={list.map((row, index) => ({
              ...row,
              index: index + 1,
              avatar: <Avatar size='small' variant='circular' alt={row?.code} src={`photos/${row?.photo}`} />,
              roles: (
                <div className='flex flex-wrap gap-1'>
                  {row?.roles?.map((role) => (
                    <Chip key={`user-${row._id}-role-${role._id}`} size='small' label={role.name} />
                  ))}
                </div>
              ),
              yearLevelUser: (
                <div className='flex flex-wrap items-center gap-1'>
                  {row?.yearlevelUser?.map((yearLevel) => (
                    <Chip
                      key={`user-${row._id}-yearLevel-${yearLevel._id}`}
                      size='small'
                      label={yearLevel.name}
                      variant='outlined'
                    />
                  ))}
                </div>
              ),
              name: `${row?.preferredName || row?.firstName} ${row?.lastName}`,
              code: row.code || '',
              checkIds: (
                <Checkbox
                  id={`checkbox-id-user-${row._id}`}
                  checked={checkedIds.includes(row._id)}
                  onChange={(e) => onCheckOne(row._id, e.target.checked)}
                  color='primary'
                  disabled={loading || isSaving}
                />
              )
            }))}
            loading={loading}
            labelNoRows='No users were found'
            nameModule='surveyUsersList'
            headers={[...columnsTable]}
            totalRows={totalRecords}
            skip={skip}
            rowsPerPage={rowsPerPage}
            orderByField='code'
            actionsSetting={
              <>
                {type === SURVEY_TYPE_STANDARD && (
                  <Typography color='primary' className='font-bold px-2'>
                    {loading ? (
                      <Skeleton width={'190px'} />
                    ) : (
                      'Please select the users that will complete the survey.'
                    )}
                  </Typography>
                )}
                {SURVEY_TYPE_ABOUT_USERS.includes(type) && (
                  <Typography color='primary' className='font-bold px-2'>
                    {loading ? (
                      <Skeleton width={'190px'} />
                    ) : (
                      ' Please select the users this survey is about.'
                    )}
                  </Typography>
                )}
                {loading ? (
                  <Skeleton width={'100px'} />
                ) : (
                  checkedIds.length > 0 && (
                    <Chip
                      className='min-w-100px'
                      align='right'
                      label={`${checkedIds.length} user(s) selected`}
                      color='primary'
                      size='small'
                      variant='outlined'
                    ></Chip>
                  )
                )}
              </>
            }
            columns={[
              {
                label: '#',
                key: 'index',
                align: 'center',
                width: 10,
                show: true,
                hide: false
              },
              {
                show: permissions['list'],
                key: 'checkIds',
                align: 'center',
                width: 15,
                label: (
                  <Checkbox
                    id={`checkbox-id-user-header`}
                    checked={checkedIds.length > 0 && checkedIds.length === totalRecords}
                    indeterminate={checkedIds.length > 0 && checkedIds.length < totalRecords}
                    onChange={(e) => onCheckedAll(e.target.checked)}
                    color='primary'
                    disabled={loading || isSaving}
                  />
                )
              },
              ...columnsTable
            ]}
          />
        </div>
      </div>
    </>
  );
};

export default TableUser;
